/**
 * useSchedulableTasks Hook
 *
 * Manages schedulable tasks via the API including AI suggestions
 * and accept/complete workflows.
 */

import { useState, useEffect, useCallback } from 'react';
import { api, SchedulableTaskDetail, SchedulableTaskInput, AiScheduleSuggestion } from '@/lib/api';
import { toast } from '@/components/ui/use-toast';

export function useSchedulableTasks() {
  const [tasks, setTasks] = useState<SchedulableTaskDetail[]>([]);
  const [loading, setLoading] = useState(true);

  const fetchTasks = useCallback(async (status?: string) => {
    try {
      setLoading(true);
      const result = await api.listSchedulableTasks(status);
      setTasks(result.tasks);
    } catch (err) {
      toast({
        title: 'Error',
        description: err instanceof Error ? err.message : 'Failed to load tasks',
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchTasks();
  }, [fetchTasks]);

  const createTask = useCallback(async (input: SchedulableTaskInput) => {
    const created = await api.createSchedulableTask(input);
    setTasks((prev) => [...prev, created]);
    return created;
  }, []);

  const updateTask = useCallback(async (id: string, input: Partial<SchedulableTaskInput>) => {
    const updated = await api.updateSchedulableTask(id, input);
    setTasks((prev) => prev.map((t) => (t.id === id ? updated : t)));
    return updated;
  }, []);

  const deleteTask = useCallback(async (id: string) => {
    await api.deleteSchedulableTask(id);
    setTasks((prev) => prev.filter((t) => t.id !== id));
  }, []);

  const suggestSchedule = useCallback(
    async (
      taskId: string,
      opts?: { workingHoursStart?: number; workingHoursEnd?: number; timezone?: string },
    ): Promise<AiScheduleSuggestion[]> => {
      const result = await api.suggestSchedule(taskId, opts);
      // Update the task's suggestions locally
      setTasks((prev) =>
        prev.map((t) => (t.id === taskId ? { ...t, aiSuggestions: result.suggestions } : t)),
      );
      return result.suggestions;
    },
    [],
  );

  const acceptSuggestion = useCallback(async (taskId: string, suggestionIndex: number) => {
    const updated = await api.acceptScheduleSuggestion(taskId, suggestionIndex);
    setTasks((prev) => prev.map((t) => (t.id === taskId ? updated : t)));
    return updated;
  }, []);

  const completeTask = useCallback(async (taskId: string) => {
    const updated = await api.completeSchedulableTask(taskId);
    setTasks((prev) => prev.map((t) => (t.id === taskId ? updated : t)));
    return updated;
  }, []);

  return {
    tasks,
    loading,
    fetchTasks,
    createTask,
    updateTask,
    deleteTask,
    suggestSchedule,
    acceptSuggestion,
    completeTask,
  };
}
